import { Injectable } from '@angular/core';
import { UserModel } from '@portal/app/shared/models/userModel';
import { environment } from '@portal/environments/environment';

interface MixpanelGlobal {
  identify: (userId: string) => void;
}

declare global {
  interface Window {
    mixpanel?: MixpanelGlobal;
  }
}

@Injectable({
  providedIn: 'root'
})
export class MixpanelService {
  /**
   * Sets up Mixpanel with the current user context.
   * Identifies the user with their email address.
   */
  public setupMixpanel(user: UserModel): void {
    if (!environment.production) {
      return;
    }

    try {
      if (this.isMixpanelReady()) {
        this.identifyMixpanel(user);
      } else {
        console.warn('Mixpanel not ready, retrying in 500ms...');
        // Retry once after a short delay to allow GTM script to load
        setTimeout(() => {
          if (this.isMixpanelReady()) {
            this.identifyMixpanel(user);
          } else {
            console.warn(
              'Mixpanel identification aborted: Mixpanel not loaded'
            );
          }
        }, 500);
      }
    } catch (error) {
      console.error('Mixpanel setup failed:', error);
    }
  }

  /**
   * Identifies the user in Mixpanel with their email address.
   */
  private identifyMixpanel(user: UserModel): void {
    const email = user.getEmail();

    if (!email) {
      console.warn('Mixpanel identification aborted: Missing email address');
      return;
    }

    window.mixpanel?.identify(email);
  }

  /**
   * Checks if Mixpanel is loaded and ready.
   */
  private isMixpanelReady(): boolean {
    try {
      return !!(
        window.mixpanel && typeof window.mixpanel.identify === 'function'
      );
    } catch (error) {
      console.warn('Error checking Mixpanel status:', error);
      return false;
    }
  }
}
